import dayjs, { type Dayjs } from 'dayjs';
import { Stack } from '@/ui';
import { DatePicker, Popover, Text } from '@/ui/primitives';
import {
  tableDatePickerOpenButtonSx,
  tableDatePickerTextFieldFontSx,
  tableDatePickerTextFieldSx,
  tableDatePopoverBodySx,
  tableDatePopoverLabelSx,
  tableDatePopoverPaperSx,
  resolveDatePreview,
  tableDateQuickRangeTextSx,
} from './tableToolbar.helpers';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';

type TableDatePopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  dateQuickFilter: DateQuickFilter;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  previewFromDate?: Dayjs | null;
  previewToDate?: Dayjs | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
};

export const TableDatePopover = ({
  open,
  anchorEl,
  onClose,
  dateQuickFilter,
  fromDate,
  toDate,
  previewFromDate = null,
  previewToDate = null,
  onFromDateChange,
  onToDateChange,
}: TableDatePopoverProps): JSX.Element => {
  const { displayedFromDate, displayedToDate, isQuickRangePreview } = resolveDatePreview(
    dateQuickFilter,
    fromDate,
    toDate,
    previewFromDate,
    previewToDate,
  );
  const isPreviewFromDate = fromDate === null && displayedFromDate !== null;
  const isPreviewToDate = toDate === null && displayedToDate !== null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      paperSx={tableDatePopoverPaperSx}
    >
      <Stack gap="none">
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap="xl"
          sx={tableDatePopoverBodySx}
        >
          <Stack gap="none" align="flex-start">
            <Text role="body" color="textSecondary" sx={tableDatePopoverLabelSx}>
              From
            </Text>
            <DatePicker
              value={displayedFromDate}
              onChange={(nextValue) => {
                const nextFromDate = nextValue?.startOf('day') ?? null;
                onFromDateChange(nextFromDate);
                if (nextFromDate !== null && toDate === null && displayedToDate !== null) {
                  onToDateChange(displayedToDate.startOf('day'));
                }
              }}
              placeholder="dd/mm/yyyy"
              lowercasePlaceholder
              maxDate={displayedToDate ?? dayjs()}
              openPickerButtonSx={tableDatePickerOpenButtonSx(fromDate !== null)}
              textFieldSx={[
                tableDatePickerTextFieldSx,
                tableDatePickerTextFieldFontSx,
                ...(isPreviewFromDate || isQuickRangePreview ? [tableDateQuickRangeTextSx] : []),
              ]}
            />
          </Stack>
          <Stack gap="none" align="flex-start">
            <Text role="body" color="textSecondary" sx={tableDatePopoverLabelSx}>
              To
            </Text>
            <DatePicker
              value={displayedToDate}
              onChange={(nextValue) => {
                if (nextValue !== null && fromDate === null && displayedFromDate !== null) {
                  onFromDateChange(displayedFromDate.startOf('day'));
                }
                onToDateChange(nextValue?.startOf('day') ?? null);
              }}
              placeholder="dd/mm/yyyy"
              lowercasePlaceholder
              minDate={displayedFromDate ?? undefined}
              maxDate={dayjs()}
              openPickerButtonSx={tableDatePickerOpenButtonSx(toDate !== null)}
              textFieldSx={[
                tableDatePickerTextFieldSx,
                tableDatePickerTextFieldFontSx,
                ...(isPreviewToDate || isQuickRangePreview ? [tableDateQuickRangeTextSx] : []),
              ]}
            />
          </Stack>
        </Stack>
      </Stack>
    </Popover>
  );
};
